import { Component, inject, signal, OnInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService, showToast } from './api.service';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-customer-declaration',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="min-h-screen bg-[#eef3f7] flex flex-col items-center p-4 md:p-8 selection:bg-[#1A6FA6] selection:text-white pb-12">
      
      <!-- Max Width Wrapper -->
      <div class="w-full max-w-5xl">
        
        <!-- Header Strip -->
        <div class="flex items-center justify-between gap-4 mb-4 text-slate-600">
           <img src="/logo.png" alt="NZ Plumbing" class="max-h-12 w-auto">
           <div class="text-right hidden sm:block">
             <p class="text-[11px] font-bold uppercase text-slate-500">Secure Customer Document</p>
             <p class="text-xs text-slate-500">Contract acceptance record</p>
           </div>
        </div>

        @if (isLoading()) {
          <div class="bg-white rounded-lg p-12 shadow-sm border border-slate-200 flex flex-col items-center justify-center text-center">
             <i class="ti ti-loader animate-spin text-[40px] text-[#2B9AD2] mb-4"></i>
             <h2 class="text-xl font-bold text-slate-800">Loading Contract...</h2>
             <p class="text-slate-500 font-medium mt-2">Retrieving your project details securely.</p>
          </div>
        } @else if (errorState()) {
          <div class="bg-white rounded-lg p-12 shadow-sm border border-slate-200 flex flex-col items-center justify-center text-center">
             <div class="w-16 h-16 bg-rose-50 rounded-lg flex items-center justify-center text-rose-500 mb-6">
                 <i class="ti ti-alert-triangle text-[32px]"></i>
             </div>
             <h2 class="text-xl font-bold text-slate-800">Link Invalid or Expired</h2>
             <p class="text-slate-500 font-medium mt-2 max-w-md">We could not load this declaration. Please review your link or contact NZ Plumbing support directly.</p>
          </div>
        } @else if (isAlreadySigned()) {
          <div class="bg-white rounded-lg p-12 shadow-sm border border-slate-200 flex flex-col items-center justify-center text-center">
             <div class="w-16 h-16 bg-emerald-50 rounded-lg flex items-center justify-center text-emerald-600 mb-6 border border-emerald-100">
                 <i class="ti ti-circle-check-filled text-[38px]"></i>
             </div>
             <h2 class="text-2xl font-bold text-slate-900">Contract Accepted</h2>
             <p class="text-slate-500 font-medium mt-3 text-lg">Thank you, {{ declarationData().customer_name }}. Your signed contract has been securely filed.</p>
             
             <button (click)="downloadPdf()" class="mt-8 px-6 py-3 bg-white border border-slate-300 text-slate-700 font-bold rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-2">
                 <i class="ti ti-file-type-pdf text-lg"></i> Open PDF Copy
             </button>
          </div>
        } @else {
          
          <div class="bg-white rounded-lg shadow-sm border border-slate-300 overflow-hidden">
            
            <!-- Context Header -->
            <div class="border-b border-slate-300 px-6 md:px-10 py-7">
              <div class="flex flex-col md:flex-row md:items-start md:justify-between gap-5">
                <div>
                  <p class="text-xs font-bold uppercase text-[#1A6FA6] mb-2">Customer Contract</p>
                  <h1 class="text-3xl font-semibold text-slate-950">Contract for Services</h1>
                  <p class="text-sm text-slate-500 mt-2 max-w-2xl">Please review the terms, confirm your details, and sign before work commences.</p>
                </div>
                <div class="border border-slate-200 rounded-lg px-4 py-3 text-sm min-w-[220px] bg-slate-50">
                  <p class="text-[11px] font-bold uppercase text-slate-500">Project Reference</p>
                  <p class="font-semibold text-slate-900 mt-1">{{ projectDetails?.id || projectId || 'Pending' }}</p>
                </div>
              </div>
              <div class="mt-6 border-t border-slate-200 pt-4">
                <p class="text-[11px] font-bold uppercase text-slate-500">Project Site Location</p>
                <p class="text-base font-semibold text-slate-900 mt-1">{{ projectDetails?.address || 'Loading...' }}</p>
              </div>
            </div>

            <!-- Form Body -->
            <div class="p-6 md:p-10">
              
              <!-- Contact Context Info -->
              <div class="flex items-center justify-between gap-4 border-b border-slate-200 pb-3 mb-5">
                <h2 class="text-base font-semibold text-slate-950">Customer Details</h2>
                <span class="text-[11px] font-bold uppercase text-slate-400">Required for acceptance</span>
              </div>
              
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                <div>
                   <label class="block text-xs font-bold text-slate-600 mb-1.5">Full Name</label>
                   <input type="text" [(ngModel)]="formData.customer_name" class="w-full h-11 px-3 rounded-lg border border-slate-300 outline-none focus:border-[#1A6FA6] focus:ring-2 focus:ring-[#1A6FA6]/15 font-medium text-slate-900 transition-all bg-white">
                </div>
                <div>
                   <label class="block text-xs font-bold text-slate-600 mb-1.5">Email Address</label>
                   <input type="email" [(ngModel)]="formData.email" class="w-full h-11 px-3 rounded-lg border border-slate-300 outline-none focus:border-[#1A6FA6] focus:ring-2 focus:ring-[#1A6FA6]/15 font-medium text-slate-900 transition-all bg-white">
                </div>
                <div class="md:col-span-2">
                   <label class="block text-xs font-bold text-slate-600 mb-1.5">Phone Number (Optional)</label>
                   <input type="text" [(ngModel)]="formData.phone" class="w-full h-11 px-3 rounded-lg border border-slate-300 outline-none focus:border-[#1A6FA6] focus:ring-2 focus:ring-[#1A6FA6]/15 font-medium text-slate-900 transition-all bg-white">
                </div>
                <div class="md:col-span-2">
                   <label class="block text-xs font-bold text-slate-600 mb-1.5">On Behalf Of (If Applicable)</label>
                   <input type="text" [(ngModel)]="formData.on_behalf_of" placeholder="Organisation, trust, company, or authorised party" class="w-full h-11 px-3 rounded-lg border border-slate-300 outline-none focus:border-[#1A6FA6] focus:ring-2 focus:ring-[#1A6FA6]/15 font-medium text-slate-900 transition-all bg-white">
                </div>
                <div>
                   <label class="block text-xs font-bold text-slate-600 mb-1.5">Building Consent Number</label>
                   <input type="text" [(ngModel)]="formData.building_consent_number" class="w-full h-11 px-3 rounded-lg border border-slate-300 outline-none focus:border-[#1A6FA6] focus:ring-2 focus:ring-[#1A6FA6]/15 font-medium text-slate-900 transition-all bg-white">
                </div>
                <div>
                   <label class="block text-xs font-bold text-slate-600 mb-1.5">Preferred Start Date</label>
                   <input type="date" [(ngModel)]="formData.preferred_start_date" class="w-full h-11 px-3 rounded-lg border border-slate-300 outline-none focus:border-[#1A6FA6] focus:ring-2 focus:ring-[#1A6FA6]/15 font-medium text-slate-900 transition-all bg-white">
                </div>
              </div>

              <!-- Terms Box -->
              <div class="flex items-center justify-between gap-4 border-b border-slate-200 pb-3 mb-5">
                <h2 class="text-base font-semibold text-slate-950">Contract Terms</h2>
                <span class="text-[11px] font-bold uppercase text-slate-400">NZ PLUMBING LTD</span>
              </div>
              
              <div class="bg-white border border-slate-300 rounded-lg p-5 md:p-6 mb-6 text-sm text-slate-700 leading-relaxed overflow-y-auto max-h-[520px] relative">
                <h3 class="text-xl font-semibold text-slate-950 mb-1">Contract for Services</h3>
                <p class="text-xs text-slate-500 mb-5">Scroll through the full terms before accepting and signing.</p>
                @for (term of contractTerms; track term.no) {
                  <section class="mb-5 last:mb-0 border-t border-slate-100 pt-4 first:border-t-0 first:pt-0">
                    <p class="text-slate-950 font-semibold mb-2">{{ term.no }} {{ term.title }}</p>
                    @for (clause of term.clauses; track clause.no) {
                      <p class="mb-2"><span class="font-semibold text-slate-900">{{ clause.no }}</span> {{ clause.text }}</p>
                    }
                  </section>
                }
              </div>

              <div (click)="acceptedTerms = !acceptedTerms" class="flex items-start gap-3 cursor-pointer group mb-8 bg-slate-50 p-4 rounded-lg border border-slate-300 select-none">
                <div class="shrink-0 mt-0.5 flex items-center justify-center w-5 h-5 rounded-md transition-all" [ngClass]="acceptedTerms ? 'border border-[#1A6FA6] bg-[#1A6FA6]' : 'border border-slate-400 bg-white group-hover:border-[#1A6FA6]'">
                  <input type="checkbox" class="sr-only" [checked]="acceptedTerms" tabindex="-1">
                  @if (acceptedTerms) { <i class="ti ti-check text-white text-[14px] font-black leading-none"></i> }
                </div>
                <span class="text-sm font-semibold text-slate-800 group-hover:text-slate-950 transition-colors">I have read and accept the Contract for Services and terms and conditions of trade.</span>
              </div>

              <!-- Signature Matrix -->
              <div class="mb-8" [class.opacity-50]="!acceptedTerms" [class.pointer-events-none]="!acceptedTerms">
                 <div class="flex items-center justify-between gap-4 border-b border-slate-200 pb-3 mb-4">
                   <h2 class="text-base font-semibold text-slate-950">Digital Signature</h2>
                   <button (click)="clearSignature()" class="text-[11px] font-bold px-3 py-1.5 bg-white border border-slate-300 text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-lg transition-colors">Clear Signature</button>
                 </div>
                 
                 <div class="border rounded-lg overflow-hidden bg-white relative transition-all cursor-crosshair"
                   [class.border-slate-300]="signatureEmpty"
                   [class.border-[#1A6FA6]]="!signatureEmpty"
                   [class.ring-2]="!signatureEmpty && acceptedTerms"
                   [class.ring-[#1A6FA6]/15]="!signatureEmpty && acceptedTerms">
                   
                   @if (signatureEmpty && !isDrawing) {
                     <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
                       <span class="text-slate-300 font-semibold uppercase text-xl opacity-60">Sign Here</span>
                     </div>
                   }
                   
                   <canvas #signaturePad 
                     class="w-full h-48 touch-none bg-transparent"
                     (mousedown)="startDrawing($event)"
                     (mousemove)="draw($event)"
                     (mouseup)="endDrawing()"
                     (mouseleave)="endDrawing()"
                     (touchstart)="startDrawing($event)"
                     (touchmove)="draw($event)"
                     (touchend)="endDrawing()"></canvas>
                 </div>
                 <p class="text-xs text-center font-semibold text-slate-500 mt-3">Finger, stylus, or mouse supported.</p>
              </div>

              <!-- Action Footer -->
              <button (click)="submitDeclaration()" [disabled]="!acceptedTerms || signatureEmpty || isSaving() || !formData.customer_name" class="w-full py-4 rounded-lg font-bold text-white text-base bg-[#14384F] hover:bg-[#0f2d40] transition-colors disabled:opacity-50 disabled:pointer-events-none flex items-center justify-center gap-2">
                 @if (isSaving()) { <i class="ti ti-loader animate-spin text-xl"></i> Finalizing... }
                 @else { <i class="ti ti-lock-check text-xl"></i> Accept and Sign Contract }
              </button>

            </div>
          </div>
          
          <div class="text-center mt-6">
            <p class="text-xs font-semibold text-slate-500">Secured via HTTPS end-to-end encryption</p>
          </div>
          
        }
      </div>
    </div>
  `
})
export class CustomerDeclarationComponent implements OnInit {
  api = inject(ApiService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  declarationKey = '';
  projectId = '';
  isLoading = signal(true);
  isSaving = signal(false);
  errorState = signal(false);
  isAlreadySigned = signal(false);

  projectDetails: any = null;
  declarationData: any = null;

  formData = {
    customer_name: '',
    email: '',
    phone: '',
    on_behalf_of: '',
    building_consent_number: '',
    preferred_start_date: '',
    signature: ''
  };

  contractTerms = [
    {
      no: '1.0',
      title: 'Incorporation of Terms',
      clauses: [
        { no: '1.1', text: 'These terms and conditions are incorporated in all contracts for the supply of goods and services ("the contracted work") by NZ PLUMBING LTD ("Company") to the Customer. They apply in place of and prevail over any terms and conditions, purchase orders or instructions, from the Customer or any terms or conditions implied by trade custom, trade practice or course of dealing. Any order by the Customer is an offer to enter into a contract with the Company subject to these terms and conditions.' },
        { no: '1.2', text: '"The Customer" means the customer, its employees or agents, and any person acting on behalf of or with the authority of the Customer.' },
        { no: '1.3', text: 'The guarantees and rights under the Consumer Guarantees Act 1993 are excluded if the Customer is acquiring the contracted work for the purposes of a business.' }
      ]
    },
    {
      no: '2.0',
      title: 'Acceptance',
      clauses: [
        { no: '2.1', text: 'Any instructions, written or oral, issued to the Company for contracted work, variations or additional services constitutes the Customer\'s acceptance of these terms and conditions.' }
      ]
    },
    {
      no: '3.0',
      title: 'Customer Warranties & Obligations',
      clauses: [
        { no: '3.1', text: 'The Customer warrants that it has obtained all necessary consents, licences and permits ("Permits") for the lawful implementation and completion of the contracted work, has informed the Company of all conditions and matters relating to such Permits and will provide copies of all such Permits to the Company upon request.' },
        { no: '3.2', text: 'The Customer must provide full and uninterrupted site access, follow the Company\'s directions, and supply or disclose all information relating to the property, including plans and information for utility services, boundary lines, land and ground conditions, hazards, contaminants, rock, subsidence, slip, slump or flood risk. The Customer indemnifies the Company for liability, claims, loss, damage, costs and fines connected with failure to comply with this subclause.' },
        { no: '3.3', text: 'The Customer must pay on demand for all additional costs, charges and expenses suffered or incurred by the Company connected with the matters recorded in clause 4.3 below.' }
      ]
    },
    {
      no: '4.0',
      title: 'Quotation',
      clauses: [
        { no: '4.1', text: 'Unless otherwise stated a quotation and the costs of the contracted work are exclusive of GST. Quotations are valid for 30 days following issue and are subject to the Company being satisfied that the Customer has a satisfactory credit rating.' },
        { no: '4.2', text: 'Unless otherwise stated a quotation excludes costs for consents or permits, variations or alterations to plans, surplus spoil disposal, contaminants, surface reinstatement, hardfill backfill, rock or concrete excavation and removal, hand excavation, unreasonable or difficult access, customer or third-party delays, and locating, redirecting or replacing existing services.' },
        { no: '4.3', text: 'The Company can charge for costs and expenses resulting from delays, suspension or stopping work, damage caused by the Customer or third parties, damage to existing services due to missing or incorrect service plans, extra work from non-disclosure or weather delays, events beyond the Company\'s control, remedying damage to Company goods or services, customer variations, and unexpected conditions encountered while undertaking the contracted work.' }
      ]
    },
    {
      no: '5.0',
      title: 'Terms of Payment',
      clauses: [
        { no: '5.1', text: 'The Customer must pay a 30% deposit on acceptance of a quote or estimate, and the balance to the Company by the twentieth (20th) of the month following the date of invoice, time being of the essence, in full and without deduction or withholding.' },
        { no: '5.2', text: 'Invoices will be issued at the end of contracted work where that work lasts less than a month. Otherwise invoices will be issued monthly.' },
        { no: '5.3', text: 'The Customer will also pay the Company the amount of GST chargeable on any taxable supply at the same time as each component of the contracted work is payable.' },
        { no: '5.4', text: 'The Company may charge interest on overdue amounts at 5% per annum above the base lending rate of its bank as at the date of default, from the due date until full payment.' },
        { no: '5.5', text: 'All payments received by the Company will be allocated by the Company as it determines in its sole and subjective opinion.' },
        { no: '5.6', text: 'The Customer is liable for all costs, charges, expenses and disbursements suffered or incurred by the Company connected with any breach, including legal costs on an indemnity basis and debt collection charges.' },
        { no: '5.7', text: 'Payment cannot be withheld due to Council, Watercare or any other third party.' }
      ]
    },
    {
      no: '6.0',
      title: 'Passing of Property and Risk',
      clauses: [
        { no: '6.1', text: 'Risk in goods supplied by the Company passes when goods are delivered to the Customer, its premises or job site, or into custody on the Customer\'s behalf, whichever is earlier. Ownership does not pass until the Customer has paid all sums owing. The Customer authorises the Company and its agents to enter property where goods are held to remove goods, without liability for entry, removal, or related damage.' }
      ]
    },
    {
      no: '7.0',
      title: 'Notice of Concerns, Complaints or Disputes',
      clauses: [
        { no: '7.1', text: 'The Customer must promptly and fully inform the Company during the contracted work of any complaints and give the Company the first and reasonable opportunity to rectify notified defects or faults.' },
        { no: '7.2', text: 'Any claim by the Customer connected with the contracted works must be made within three calendar months of completion. If not brought within that period, the Customer is deemed to have waived and abandoned all claims.' },
        { no: '7.3', text: 'If the Customer fails to notify and give the Company reasonable opportunity to rectify defects or faults, the Company will not be liable for costs incurred by the Customer in remedying purported defects or faults, nor allow deduction or back-charge.' }
      ]
    },
    {
      no: '8.0',
      title: 'Resolution of Dispute',
      clauses: [
        { no: '8.1', text: 'If the Company considers the appropriate forum to determine a dispute is the Disputes Tribunal or similar body, the Customer agrees to that Tribunal having extended jurisdiction up to the maximum permitted sum.' }
      ]
    },
    {
      no: '9.0',
      title: 'Limitation of Liability',
      clauses: [
        { no: '9.1', text: 'If the Company is liable to the Customer, the Company\'s maximum aggregate liability connected with the contracted work is limited to the lesser of $10,000 or the cost of remedying such defects.' },
        { no: '9.2', text: 'The Company is only liable for direct and foreseeable losses, damages, costs and expenses, and is not liable for delays or failure caused by circumstances beyond its control or acts or omissions of third parties.' },
        { no: '9.3', text: 'In no circumstances will the Company determine the fitness of design or fitness of ground conditions for the contracted work.' },
        { no: '9.4', text: 'The Company is not liable for costs, expenses, damage or loss caused directly or indirectly by design, weather conditions, or structures or ground conditions encountered without comprehensive and accurate information and instructions from an appropriately qualified engineer.' }
      ]
    },
    {
      no: '10.0',
      title: 'Security and Charge',
      clauses: [
        { no: '10.1', text: 'If required by the Company, the Customer will mortgage and charge all interests in land in favour of the Company to secure all obligations. The applicable memorandum of terms will be the latest all obligations Auckland Law Society Inc mortgage terms. The Customer irrevocably appoints the Company its attorney for executing or registering any mortgage if the Customer fails to do so when requested, and indemnifies the Company for costs, expenses, losses or damages connected with exercising rights under this clause.' }
      ]
    },
    {
      no: '11.0',
      title: 'Miscellaneous Provisions',
      clauses: [
        { no: '11.1', text: 'The Customer must pay all costs and expenses incurred or suffered by the Company connected with the Company being prevented from completing contracted work caused by the Customer or third parties beyond the Company\'s control.' },
        { no: '11.2', text: 'The Company will not be deemed to have waived any terms, conditions or rights unless waived in writing. Waiver of one right does not waive any other rights.' },
        { no: '11.3', text: 'If any provision is declared invalid, void or unenforceable, that provision is severable and all remaining provisions remain valid and enforceable.' },
        { no: '11.4', text: 'To the full extent possible, all implied warranties, conditions and terms are excluded. These terms replace and supersede all prior agreements, representations, undertakings, warranties, covenants and terms.' },
        { no: '11.5', text: 'The Company can change these terms and conditions at any time. Updated terms are binding and deemed accepted when the Customer places an order for further services or continues to engage the Company, whichever is earlier.' },
        { no: '11.6', text: 'No deletions or amendments by any person other than the Company will have effect and are deemed reinstated unaltered.' },
        { no: '11.7', text: 'The Company can obtain and exchange information for assessing the Customer\'s or Guarantor\'s credit worthiness, notifying default, or advising other credit providers about the status of any credit account.' },
        { no: '11.8', text: 'The Customer\'s obligations and liability to the Company are joint and several if more than one person.' },
        { no: '11.9', text: 'The rights or remedies of the Company under these terms are without prejudice to, and do not limit, any other rights or remedies of the Company.' },
        { no: '11.10', text: 'The Construction Contracts Act 2002 applies to any work undertaken by the Company. To the extent that these terms are not permitted by the CCA, the CCA provisions prevail.' }
      ]
    },
    {
      no: '12.0',
      title: 'Suspension / Termination',
      clauses: [
        { no: '12.1', text: 'The Company may suspend, stop or cancel all or part of the contracted works without penalty or liability if the Customer becomes insolvent, a liquidator, administrator or similar person is appointed, the Company considers the Customer may be unable to meet obligations, the Customer breaches these terms, or the Company is frustrated or prevented from performing by third parties or events beyond its control. The Customer will immediately pay for all work undertaken up to that date.' },
        { no: '12.2', text: 'Termination is without prejudice to accrued rights or remedies of any party at the date of termination. Please read the terms and conditions of trade and return the attached form to acknowledge acceptance. All work is performed pursuant to the terms and conditions of trade and the Construction Contracts Act 2002.' }
      ]
    }
  ];

  acceptedTerms = false;

  // Signature PAD Logic Extracted
  @ViewChild('signaturePad') signaturePad?: ElementRef<HTMLCanvasElement>;
  isDrawing = false;
  signatureEmpty = true;
  lastCanvasPos = { x: 0, y: 0 };
  canvasInitialized = false;

  ngOnInit() {
    this.declarationKey = this.route.snapshot.paramMap.get('id') || '';
    if (!this.declarationKey) {
      this.errorState.set(true);
      this.isLoading.set(false);
      return;
    }
    this.loadContext();
  }

  loadContext() {
    this.api.getProjectDeclaration(this.declarationKey).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.projectDetails = res.data.project;
          this.projectId = res.data.project?.id || '';
          this.declarationData = res.data.declaration;

          if (res.data.is_signed) {
            this.isAlreadySigned.set(true);
          } else {
            // Pre-fill if we have an explicit project customer name natively mapped
            if (this.projectDetails.customer_name) {
              this.formData.customer_name = this.projectDetails.customer_name;
            }
            if (res.data.declaration) {
              this.formData.customer_name = res.data.declaration.customer_name || this.formData.customer_name;
              this.formData.email = res.data.declaration.email || this.formData.email;
              this.formData.phone = res.data.declaration.phone || this.formData.phone;
              this.formData.on_behalf_of = res.data.declaration.on_behalf_of || this.formData.on_behalf_of;
              this.formData.building_consent_number = res.data.declaration.building_consent_number || this.formData.building_consent_number;
              this.formData.preferred_start_date = res.data.declaration.preferred_start_date || this.formData.preferred_start_date;
            }
            // Delay Canvas init slightly for UI to mount
            setTimeout(() => { this.initCanvas(); }, 100);
          }
        } else {
          this.errorState.set(true);
        }
        this.isLoading.set(false);
      },
      error: () => {
        this.errorState.set(true);
        this.isLoading.set(false);
      }
    });
  }

  initCanvas() {
    if (!this.signaturePad || this.canvasInitialized) return;
    const canvas = this.signaturePad.nativeElement;
    // Retina scaling for sharper signatures natively mapped
    const rect = canvas.parentElement!.getBoundingClientRect();
    canvas.width = rect.width * 2;
    canvas.height = 192 * 2; // 48 tailwind rems ~ 192px height bound
    canvas.style.width = '100%';
    canvas.style.height = '192px';
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(2, 2);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.lineWidth = 4;
      ctx.strokeStyle = '#1e293b'; // Sharp slate-800 matrix
    }
    this.canvasInitialized = true;
  }

  // Bind Native Touch & Mouse Tracks globally extracting offsets reliably
  getCanvasPos(e: MouseEvent | TouchEvent) {
    if (!this.signaturePad) return { x: 0, y: 0 };
    const canvas = this.signaturePad.nativeElement;
    const rect = canvas.getBoundingClientRect();
    let clientX, clientY;

    if (e instanceof MouseEvent) {
      clientX = e.clientX;
      clientY = e.clientY;
    } else {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
      if (e.cancelable) e.preventDefault(); // Stop mobile scroll matrix actively binding
    }

    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  }

  startDrawing(e: MouseEvent | TouchEvent) {
    if (!this.acceptedTerms) return; // Lock track mapping if terms unchecked
    if (!this.canvasInitialized) this.initCanvas();
    this.isDrawing = true;
    this.signatureEmpty = false;
    this.lastCanvasPos = this.getCanvasPos(e);
  }

  draw(e: MouseEvent | TouchEvent) {
    if (!this.isDrawing || !this.signaturePad) return;
    const pos = this.getCanvasPos(e);
    const ctx = this.signaturePad.nativeElement.getContext('2d');
    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(this.lastCanvasPos.x, this.lastCanvasPos.y);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    }
    this.lastCanvasPos = pos;
  }

  endDrawing() {
    this.isDrawing = false;
  }

  clearSignature() {
    if (!this.signaturePad) return;
    const canvas = this.signaturePad.nativeElement;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    this.signatureEmpty = true;
  }

  submitDeclaration() {
    if (this.signatureEmpty || !this.signaturePad || !this.acceptedTerms) return;

    this.isSaving.set(true);

    // Extract base64
    this.formData.signature = this.signaturePad.nativeElement.toDataURL('image/png');

    const payload = {
      project_id: this.projectId,
      public_token: this.declarationData?.public_token || this.declarationKey,
      customer_name: this.formData.customer_name,
      email: this.formData.email,
      phone: this.formData.phone,
      on_behalf_of: this.formData.on_behalf_of,
      building_consent_number: this.formData.building_consent_number,
      preferred_start_date: this.formData.preferred_start_date,
      signature: this.formData.signature
    };

    this.api.saveProjectDeclaration(payload).subscribe({
      next: (res: any) => {
        if (res.success) {
          showToast('success', 'Declaration securely filed!');
          this.loadContext(); // Reload matrix to flip UI into Success state
        } else {
          showToast('error', res.message || 'Failed to file declaration.');
          this.isSaving.set(false);
        }
      },
      error: () => {
        showToast('error', 'Network error tracking declaration submission.');
        this.isSaving.set(false);
      }
    });
  }

  downloadPdf() {
    this.router.navigate(['/declaration-pdf', this.declarationData?.public_token || this.declarationKey]);
  }
}
